import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  MapPin, 
  Calendar, 
  Users, 
  Ruler, 
  Euro,
  Clock,
  CheckCircle,
  Play,
  X
} from 'lucide-react';
import { fetchProject } from '../lib/contentApi';
import { projectCategories } from '../lib/projectCategories';
import { getProjectIdFromSlug } from '../lib/projectSlug';

export const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchProject(getProjectIdFromSlug(id))
      .then(setProject)
      .catch((err) => {
        setProject(null);
        setError(err.message || 'Projeto não encontrado.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!loading && !project && !error) {
      navigate('/projetos', { replace: true });
    }
  }, [project, loading, error, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-neutral-600">A carregar projeto…</div>;
  }

  if (error || !project) {
    return <div className="min-h-screen flex items-center justify-center text-red-700">{error || 'Projeto não encontrado.'}</div>;
  }

  const categoryName = projectCategories.find(cat => cat.id === project.category)?.name;

  const hasLocation = Boolean(project.location);
  const hasYear = Boolean(project.year);
  const hasDuration = Boolean(project.duration);
  const hasClient = Boolean(project.client);
  const hasArea = Boolean(project.area);
  const hasBudget = Boolean(project.budget);
  const hasAnyInfoCard =
    hasLocation || hasYear || hasDuration || hasClient || hasArea || hasBudget;

  const hasFullDescription = Boolean(project.fullDescription);
  const hasChallenge = Boolean(project.challenge);
  const hasSolution = Boolean(project.solution);
  const hasMaterials =
    Array.isArray(project.materials) && project.materials.length > 0;
  const hasImages = Array.isArray(project.images) && project.images.length > 0;
  const hasResults =
    Array.isArray(project.results) && project.results.length > 0;
  const videoThumbnailSrc =
    project.images?.[0] ?? (project.image ? project.image : null);

  return (
    <div className="min-h-screen">
      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-[#262d37] z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-neutral-300 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={selectedImage} 
            alt={`${project.title} — imagem ampliada`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        {project.image ? (
          <img 
            src={project.image} 
            alt={project.title}
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-neutral-800" aria-hidden />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 pb-16 md:pb-20 pt-0">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            {categoryName && (
              <Badge className="mb-6 bg-[#f2f1ec]/90 text-black hover:bg-[#e8e6df]">
                {categoryName}
              </Badge>
            )}
            {project.title && (
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                {project.title}
              </h1>
            )}
            {project.description && (
              <p className="text-base md:text-lg text-neutral-200 max-w-3xl leading-relaxed">
                {project.description}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Project Info Cards */}
      {hasAnyInfoCard && (
        <section className="py-10 bg-[#e8e6df]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8">
              {hasLocation && (
                <Card className="border-neutral-200">
                  <CardContent className="p-6 text-center">
                    <MapPin className="mx-auto mb-3 text-neutral-600" size={24} />
                    <p className="text-xs text-neutral-500 mb-1">Localização</p>
                    <p className="font-semibold text-sm">{project.location}</p>
                  </CardContent>
                </Card>
              )}
              {hasYear && (
                <Card className="border-neutral-200">
                  <CardContent className="p-6 text-center">
                    <Calendar className="mx-auto mb-3 text-neutral-600" size={24} />
                    <p className="text-xs text-neutral-500 mb-1">Ano</p>
                    <p className="font-semibold text-sm">{project.year}</p>
                  </CardContent>
                </Card>
              )}
              {hasDuration && (
                <Card className="border-neutral-200">
                  <CardContent className="p-6 text-center">
                    <Clock className="mx-auto mb-3 text-neutral-600" size={24} />
                    <p className="text-xs text-neutral-500 mb-1">Duração</p>
                    <p className="font-semibold text-sm">{project.duration}</p>
                  </CardContent>
                </Card>
              )}
              {hasClient && (
                <Card className="border-neutral-200">
                  <CardContent className="p-6 text-center">
                    <Users className="mx-auto mb-3 text-neutral-600" size={24} />
                    <p className="text-xs text-neutral-500 mb-1">Cliente</p>
                    <p className="font-semibold text-sm">{project.client}</p>
                  </CardContent>
                </Card>
              )}
              {hasArea && (
                <Card className="border-neutral-200">
                  <CardContent className="p-6 text-center">
                    <Ruler className="mx-auto mb-3 text-neutral-600" size={24} />
                    <p className="text-xs text-neutral-500 mb-1">Área</p>
                    <p className="font-semibold text-sm">{project.area}</p>
                  </CardContent>
                </Card>
              )}
              {hasBudget && (
                <Card className="border-neutral-200">
                  <CardContent className="p-6 text-center">
                    <Euro className="mx-auto mb-3 text-neutral-600" size={24} />
                    <p className="text-xs text-neutral-500 mb-1">Investimento</p>
                    <p className="font-semibold text-sm">{project.budget}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-10 bg-[#f2f1ec]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-16">
              {/* Full Description */}
              {hasFullDescription && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-black mb-3">Sobre o Projeto</h2>
                  <p className="text-base md:text-lg text-neutral-600 leading-relaxed">
                    {project.fullDescription}
                  </p>
                </div>
              )}

              {/* Challenge & Solution */}
              {(hasChallenge || hasSolution) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {hasChallenge && (
                    <Card>
                      <CardContent className="p-8">
                        <h3 className="text-xl font-semibold text-black mb-2">O Desafio</h3>
                        <p className="text-neutral-600 leading-relaxed">
                          {project.challenge}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                  {hasSolution && (
                    <Card>
                      <CardContent className="p-8">
                        <h3 className="text-xl font-semibold text-black mb-2">A Solução</h3>
                        <p className="text-neutral-600 leading-relaxed">
                          {project.solution}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Materials */}
              {hasMaterials && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-black mb-3">Materiais Fornecidos</h2>
                  <div className="space-y-6">
                    {project.materials.map((material, index) => (
                      <Card key={index} className="border-neutral-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-8">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg text-black mb-2">
                                {material.name}
                              </h4>
                              {material.quantity && (
                                <p className="text-neutral-600 mb-2">
                                  <span className="font-medium">Quantidade:</span> {material.quantity}
                                </p>
                              )}
                              {material.specs && (
                                <p className="text-sm text-neutral-500">
                                  {material.specs}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Video Section */}
              {project.video && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-black mb-3">Vídeo do Projeto</h2>
                  {!showVideo ? (
                    <div 
                      className="relative h-96 bg-neutral-900 rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => setShowVideo(true)}
                    >
                      {videoThumbnailSrc ? (
                        <img 
                          src={videoThumbnailSrc} 
                          alt={`Vídeo do projeto ${project.title}`}
                          className="w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity"
                        />
                      ) : (
                        <div className="w-full h-full bg-neutral-800" aria-hidden />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 bg-[#f2f1ec] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="text-black ml-1" size={32} />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white text-base font-medium">Ver Vídeo do Projeto</p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        src={project.video}
                        className="absolute inset-0 w-full h-full rounded-lg"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Image Gallery */}
              {hasImages && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-black mb-3">Galeria de Imagens</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                    {project.images.map((image, index) => (
                      <div 
                        key={index} 
                        className="relative h-64 overflow-hidden rounded-lg cursor-pointer group"
                        onClick={() => setSelectedImage(image)}
                      >
                        <img 
                          src={image} 
                          alt={`${project.title} — galeria ${index + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-[#262d37]/0 group-hover:bg-[#262d37]/20 transition-colors" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              <Card className="border-neutral-200 sticky top-24">
                <CardContent className="p-8">
                  {hasResults && (
                    <>
                      <h3 className="text-xl font-bold text-black mb-3">Resultados</h3>
                      <div className="space-y-6">
                        {project.results.map((result, index) => (
                          <div key={index} className="flex items-start gap-4">
                            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                            <p className="text-neutral-600 leading-relaxed">{result}</p>
                          </div>
                        ))}
                      </div>
                      <Separator className="my-8" />
                    </>
                  )}
                  <Link to="/contactos">
                    <Button className="w-full bg-[#262d37] text-white hover:bg-neutral-800">
                      Solicitar Orçamento Similar
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
